import React from 'react';
import { SplitSquareVertical } from 'lucide-react';

export function PageRenderer({
  page, pageIndex, activePageIndex, onPageChange,
  pageW, pageH, scale, margins, contentW, contentH,
  headerConfig, footerConfig, headerH, footerH, bodyH,
  showGrid, snapSize,
  renderHeader, renderPageContent, renderFooter, children,
  pages,
}) {
  return (
    <div
      data-page-index={pageIndex}
      data-page-id={page.id}
      className={`relative flex-shrink-0 transition-all ${pageIndex === activePageIndex ? 'ring-2 ring-primary/30 ring-offset-4 ring-offset-muted/30' : 'opacity-70 hover:opacity-90'}`}
      onClick={(e) => { e.stopPropagation(); onPageChange && onPageChange(pageIndex); }}
    >
      {page.hasManualBreak && pageIndex > 0 && (
        <div className="absolute -top-5 left-0 right-0 flex items-center justify-center">
          <div className="flex items-center gap-2 bg-amber-500/20 px-2 py-0.5 rounded-full">
            <SplitSquareVertical className="w-3 h-3 text-amber-500" />
            <span className="text-[8px] text-amber-600 font-medium">Page Break</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-sm shadow-2xl relative" style={{ width: pageW * scale, height: pageH * scale }} data-testid={`report-page-${pageIndex}`}>
        <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: pageW, height: pageH, position: 'relative' }}>
          {showGrid && (
            <>
              <div className="absolute border border-dashed border-blue-300/20 pointer-events-none" style={{ top: margins?.top || 48, left: margins?.left || 48, width: contentW, height: contentH }} />
              {(snapSize || 0) > 0 && (
                <svg className="absolute inset-0 pointer-events-none" width={pageW} height={pageH} style={{ opacity: 0.15 }}>
                  {Array.from({ length: Math.ceil(pageW / snapSize) }).map((_, xi) =>
                    Array.from({ length: Math.ceil(pageH / snapSize) }).map((_, yi) => (
                      <circle key={`${xi}-${yi}`} cx={xi * snapSize} cy={yi * snapSize} r={0.5} fill="currentColor" />
                    ))
                  )}
                </svg>
              )}
            </>
          )}

          <div style={{ position: 'absolute', top: margins?.top || 48, left: margins?.left || 48, width: contentW, height: contentH }}>
            {headerConfig?.enabled && (headerConfig.showOnFirstPage || pageIndex > 0) && (
              <div className="absolute left-0 right-0 top-0 border-b border-dashed border-sky-400/30" style={{ height: headerH }} data-zone="header">
                <span className="absolute top-0.5 right-1 text-[7px] text-sky-400/40 font-semibold uppercase pointer-events-none">Page Header</span>
                {renderHeader && renderHeader(pageIndex, page)}
              </div>
            )}

            <div className="absolute left-0 right-0" style={{ top: headerH, height: bodyH }} data-zone="body">
              {renderPageContent ? renderPageContent(pageIndex, page, { bodyH, contentW }) : (pageIndex === activePageIndex && children)}
            </div>

            {footerConfig?.enabled && (
              <div className="absolute left-0 right-0 bottom-0 border-t border-dashed border-sky-400/30" style={{ height: footerH }} data-zone="footer">
                <span className="absolute top-0.5 right-1 text-[7px] text-sky-400/40 font-semibold uppercase pointer-events-none">Page Footer</span>
                {renderFooter && renderFooter(pageIndex, page)}
                {!renderFooter && (() => {
                  // Default page-number footer when the user hasn't added
                  // their own footer visuals. Style is driven by
                  // footerConfig.pageNumberFormat (none|simple|of_n|verbose).
                  // Falling back to the legacy 'verbose' for backwards-compat.
                  const fmt = footerConfig.pageNumberFormat || 'verbose';
                  const map = {
                    none:    null,
                    simple:  `${pageIndex + 1}`,
                    of_n:    `${pageIndex + 1} of ${pages.length}`,
                    verbose: `Page ${pageIndex + 1} of ${pages.length}`,
                  };
                  const txt = map[fmt];
                  if (!txt) return null;
                  return (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[9px] text-gray-400">{txt}</span>
                    </div>
                  );
                })()}
                {!renderFooter && footerConfig.showGeneratedAt && (
                  // Optional "Generated <date>" stamp at the bottom-left.
                  // Independent of pageNumberFormat so users can have both.
                  <div className="absolute left-3 bottom-1">
                    <span className="text-[8px] text-gray-400/70">
                      Generated {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="absolute -bottom-6 left-0 right-0 flex justify-center pointer-events-none">
            <span className="text-[9px] text-muted-foreground/70 bg-background/80 px-2 py-0.5 rounded">{page.name || `Page ${pageIndex + 1}`}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
